import { createError } from "core/exceptions";
import log from "loglevel";
import { validObject } from "./validation";
export const ACTION_STATES = {
    NO_REQUEST: "NO_REQUEST",
    SEND: "SEND",
    SUCCESS: "SUCCESS",
    FAILED: "FAILED",
};
export const isLoadingStatus = (status) => status === ACTION_STATES.SEND;
export class DefaultAction {
    name = "DefaultAction";
    attribute = null;
    props = {
        error: true,
    };
    type = ACTION_STATES.SEND;
    constructor(attribute, props) {
        this.attribute = attribute;
        this.name = this.constructor.name;
        const atr = this.attribute ? this.attribute : new Date().getTime();
        this.name = _getActionName(this.constructor.name, "DefaultAction", this.name, atr);
        if (props) {
            this.props = {
                ...this.props,
                ...props,
            };
        }
        this.type = createTypeStr(this.name, ACTION_STATES.SEND);
    }
    call(payload) {
        return {
            type: this.type,
            payload,
        };
    }
    callRdx(state, action) {
        if (this.attribute) {
            return Object.assign({}, state, { [this.attribute]: action.payload });
        } else {
            return state;
        }
    }
}

function createTypeStr(name, type) {
    return `${name}@TYPE:${type}`;
}
function _getActionName(constructorName, defaultConstructorName, defaultName, attribute) {
    const hasConstructorName = constructorName === defaultConstructorName;
    if (defaultName) {
        return defaultName + `-${attribute}`;
    } else {
        if (hasConstructorName) {
            return `action-${constructorName}-${attribute}`;
        } else {
            return `action-${constructorName}`;
        }
    }
}
export class ComplexAction {
    name = "ComplexAction";
    sendType;
    successType;
    failedType;
    props = {
        loadingAtr: null,
        error: true,
    };
    constructor(props) {
        this.name = this.constructor.name;
        if (props) {
            this.props = {
                ...this.props,
                ...props,
            };
        }
        const loadingPath = this.props.loadingAtr || "actionAtr";
        this.name = this.name = _getActionName(
            this.name,
            "ComplexAction",
            this.name,
            `${loadingPath}-${new Date().getTime()}`
        );
        this.sendType = createTypeStr(this.name, ACTION_STATES.SEND);
        this.successType = createTypeStr(this.name, ACTION_STATES.SUCCESS);
        this.failedType = createTypeStr(this.name, ACTION_STATES.FAILED);
    }

    sendRdx(state) {
        if (this.props.loadingAtr) {
            return Object.assign({}, state, { [this.props.loadingAtr]: ACTION_STATES.SEND });
        }
        return state;
    }
    send(payload) {
        return {
            type: this.sendType,
            payload,
        };
    }

    successRdx(state, { payload }) {
        if (this.props.loadingAtr) {
            return Object.assign({}, state, { [this.props.loadingAtr]: ACTION_STATES.SUCCESS, ...payload });
        }
        return Object.assign({}, state, { ...payload });
    }
    success(payload) {
        return {
            type: this.successType,
            payload,
        };
    }

    failedRdx(state, { payload }) {
        if (this.props.error) {
            return Object.assign({}, state, { [this.props.loadingAtr]: ACTION_STATES.FAILED, ...payload });
        }
        return state;
    }
    failed(payload, error) {
        if (payload && payload.logicError) {
            return {
                type: this.failedType,
                payload: {
                    ...payload,
                },
            };
        }
        let finalError = error && !error.type ? createError(error) : error;
        log.error(
            this.failedType,
            finalError,
            finalError ? finalError.message : `No error messages for  ${this.name} ${this.failedType}`
        );
        return {
            type: this.failedType,
            payload: {
                ...payload,
                error: finalError,
            },
        };
    }
}

export class BasicAjaxAction extends ComplexAction {
    name = "BasicAjaxAction";
    attribute = null;
    apiCall = null;
    props = {
        attribute: null,
        debounce: null,
        error: true,
    };
    constructor(name, attribute, apiCall, props = {}) {
        super({
            loadingAtr: props.loadingAtr,
            error: true,
        });

        this.attribute = attribute ? attribute : this.props.attribute;
        this.name = _getActionName(name, "BasicAjaxAction", name, this.attribute);

        if (!validObject(apiCall)) {
            throw new Error(`Unable to create ${this.name} server api call is null`);
        }
        this.apiCall = apiCall;
        this.sendType = createTypeStr(this.name, ACTION_STATES.SEND);
        this.successType = createTypeStr(this.name, ACTION_STATES.SUCCESS);
        this.failedType = createTypeStr(this.name, ACTION_STATES.FAILED);
        if (props) {
            this.props = {
                ...this.props,
                ...props,
            };
        }
    }
    successRdx(state, { payload }) {
        const newState = {};
        if (this.attribute) {
            newState[this.props.attribute] = payload.data;
            if (this.props.loadingAtr) {
                return Object.assign({}, state, {
                    [this.attribute]: payload.data,
                    [this.props.loadingAtr]: ACTION_STATES.SUCCESS,
                });
            }
            return Object.assign({}, state, { [this.attribute]: payload.data });
        } else {
            if (this.props.loadingAtr) {
                return Object.assign({}, state, { ...payload, [this.props.loadingAtr]: ACTION_STATES.SUCCESS });
            }
            return Object.assign({}, state, { ...payload });
        }
    }

    failedRdx(state, payload) {
        log.debug(payload, this.props);
        if (this.props.error) {
            return Object.assign({}, state, { [this.props.loadingAtr]: ACTION_STATES.FAILED, ...payload });
        }
        return state;
    }
}

export function createHandler(actions) {
    const handler = {};
    if (validObject(actions)) {
        Object.keys(actions).map((key) => {
            const classInstance = actions[key];
            if (classInstance instanceof ComplexAction || classInstance instanceof BasicAjaxAction) {
                handler[classInstance.sendType] = classInstance.sendRdx.bind(classInstance);
                handler[classInstance.successType] = classInstance.successRdx.bind(classInstance);
                handler[classInstance.failedType] = classInstance.failedRdx.bind(classInstance);
            } else if (classInstance instanceof DefaultAction) {
                handler[classInstance.type] = classInstance.callRdx.bind(classInstance);
            } else {
                handler[key] = actions[key];
            }
        });
    }
    return handler;
}

export function createReducer(initialState, handlers) {
    return function reducer(state = initialState, action) {
        // eslint-disable-next-line no-prototype-builtins
        if (handlers.hasOwnProperty(action.type)) {
            return handlers[action.type](state, action);
        } else {
            return state;
        }
    };
}
